/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo, useRef } from 'react';
import { Box, FormLabel, ListItemText, MenuItem, Select, Tooltip } from '@mui/material';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { minimalLabelTheme } from '@/app/theme';
import { usePipelineContext } from '@/context/pipelineContext';
import { useChipFit } from '@/hooks/useChipFit';
import { OverflowChips } from '@/components/OverflowChips';

export const DealStageListSelect = ({
	disabled,
	multiple = true,
	width = '150px',
	pipeline,
	minimal = true,
	filters = { dealStage: [], status: [] },
	onFilterChange,
	...props
}) => {

	const dealStageFilter = filters.dealStage ?? [];
	const statusFilter = filters.status ?? [];

	const containerRef = useRef();

	const { active, closed, completed } = usePipelineContext(pipeline);
	const { activeIds, completedIds, closedIds, inProgressIds } = useMemo(() => {
		const activeIds = active.map((stage) => stage.id.toString());
		const completedIds = completed.map((stage) => stage.id.toString());
		const closedIds = closed.map((stage) => stage.id.toString());
		return {
			activeIds,
			completedIds,
			closedIds,
			inProgressIds: activeIds.filter((id) => !completedIds.includes(id)),
		};
	}, [active, completed, closed]);

	const statusOptions = useMemo(() => {
		return [
			{ label: 'Completed', value: 'inTerm', stageIds: completedIds, type: 'stage' },
			{ label: 'In Progress', value: 'inProgress', stageIds: inProgressIds, type: 'stage' },
			{ label: 'Lost', value: 'closed', stageIds: closedIds, type: 'stage' },
			{ label: 'Archived', value: 'Archived', type: 'status' },
		];
	}, [completedIds, inProgressIds, closedIds]);

	const allStageIds = useMemo(
		() => [...activeIds, ...completedIds, ...closedIds],
		[activeIds, completedIds, closedIds],
	);

	const none = useMemo(
		() => ({
			label: 'Clear Filters',
			value: 'None',
			isRefined: dealStageFilter.length === 0 && statusFilter.length === 0,
		}),
		[dealStageFilter, statusFilter],
	);

	const allItems = useMemo(() => {
		return [
			none,
			...statusOptions.map((option) => ({
				...option,
				isRefined:
					option.type === 'status'
						? filters.status.includes('Archived')
						: option.stageIds.every((id) => dealStageFilter.includes(id)),
			})),
		];
	}, [none, statusOptions, dealStageFilter, statusFilter]);

	const selectedValues = useMemo(() => {
		if (multiple) {
			return statusOptions
				.filter((opt) => {
					if (opt.type === 'status') return statusFilter.includes('Archived');
					return opt.stageIds.every((id) => dealStageFilter.includes(id));
				})
				.map((opt) => opt.value);
		}

		if (statusFilter.includes('Archived')) return 'Archived';

		const selectedOption = statusOptions.find(
			(opt) =>
				opt.type === 'stage' && opt.stageIds.every((id) => dealStageFilter.includes(id)),
		);
		return selectedOption ? selectedOption.value : 'None';
	}, [multiple, statusOptions, dealStageFilter, statusFilter]);

	const limitTags = useChipFit(containerRef, selectedValues.length);

	const handleChange = (event) => {
		let values = event.target.value;
		if (!multiple) values = [values];

		let newStageFilter = [];
		let newStatusFilter = [];
		let isActiveStage = false;
		let isInTermStage = false;

		if (values.includes('All')) {
			newStageFilter = allStageIds;
		} else if (values.includes('None')) {
			newStageFilter = [];
			newStatusFilter = [];
		} else {
			const isArchivedSelected = values.includes('Archived');
			const selectedStageValues = values.filter((v) => v !== 'Archived');
			newStageFilter = statusOptions
				.filter((opt) => selectedStageValues.includes(opt.value))
				.flatMap((opt) => opt.stageIds);

			newStatusFilter = isArchivedSelected ? ['Archived'] : [];

			if (selectedStageValues.includes('inProgress')) {
				isActiveStage = true;
			}
			if (selectedStageValues.includes('inTerm')) {
				isInTermStage = true;
			}
		}

		onFilterChange({
			dealStage: newStageFilter,
			status: newStatusFilter,
			isActiveStage,
			isInTermStage,
		});
	};

	return (
		<Box width={width}>
			<FormLabel>{'Deal status'}</FormLabel>
			<Tooltip
				disableFocusListener
				title={statusOptions
					.filter((opt) => {
						if (opt.type === 'status') return statusFilter.includes('Archived');
						return opt.stageIds.every((id) => dealStageFilter.includes(id));
					})
					.map((opt) => opt.label)
					.join(', ')}
				placement='top'
			>
				<Select
					ref={containerRef}
					multiple={multiple}
					fullWidth
					disabled={disabled}
					value={selectedValues}
					onChange={handleChange}
					sx={{
						minHeight: 35,
						display: 'flex',
						alignItems: 'center',
					}}
					renderValue={(selected) =>
						multiple ? (
							<OverflowChips
								items={selected}
								limitTags={limitTags}
								getLabel={(val) =>
									statusOptions.find((opt) => opt.value === val)?.label || val
								}
							/>
						) : (
							statusOptions.find((opt) => opt.value === selected)?.label || 'None'
						)
					}
				>
					{allItems.map((item) => {
						return (
							<MenuItem key={`select-${item.label}`} value={item.value}>
								{multiple &&
									(item.isRefined ? <CheckBox /> : <CheckBoxOutlineBlank />)}
								<ListItemText primary={item.label} />
							</MenuItem>
						);
					})}
				</Select>
			</Tooltip>
		</Box>
	);
};
